import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Sparkles, ArrowLeft, BarChart3, Users, Search, Image, TrendingUp, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searchAnalytics, setSearchAnalytics] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [searchRes, userRes] = await Promise.all([
        axios.get(`${API}/analytics/searches`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/analytics/users`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setSearchAnalytics(searchRes.data);
      setUserAnalytics(userRes.data);
    } catch (error) {
      toast.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin text-primary">
          <Sparkles size={32} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-background to-black">
      <nav className="border-b border-border/50 backdrop-blur-md bg-card/30 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-button">
              <ArrowLeft size={20} />
            </Button>
            <div className="flex items-center gap-2">
              <BarChart3 className="text-primary" size={24} />
              <h1 className="text-xl font-heading font-bold text-foreground">Analytics</h1>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-6 rounded-xl border bg-card/50 backdrop-blur-md" data-testid="total-searches-card">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-primary/10">
                <Search className="text-primary" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Searches</p>
                <p className="text-2xl font-heading font-bold text-foreground">
                  {searchAnalytics?.total_searches?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6 rounded-xl border bg-card/50 backdrop-blur-md" data-testid="total-users-card">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-500/10">
                <Users className="text-green-500" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Users</p>
                <p className="text-2xl font-heading font-bold text-foreground">
                  {userAnalytics?.total_users?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6 rounded-xl border bg-card/50 backdrop-blur-md" data-testid="image-searches-card">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-purple-500/10">
                <Image className="text-purple-500" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Image Searches</p>
                <p className="text-2xl font-heading font-bold text-foreground">
                  {searchAnalytics?.image_searches?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6 rounded-xl border bg-card/50 backdrop-blur-md" data-testid="active-users-card">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-orange-500/10">
                <TrendingUp className="text-orange-500" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Users</p>
                <p className="text-2xl font-heading font-bold text-foreground">
                  {userAnalytics?.active_users?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Daily Searches Chart */}
          <Card className="p-6 rounded-xl border bg-card/50 backdrop-blur-md" data-testid="daily-searches-card">
            <div className="flex items-center gap-3 mb-6">
              <Calendar className="text-primary" size={20} />
              <h2 className="text-lg font-heading font-semibold text-foreground">Daily Searches (Last 7 Days)</h2>
            </div>
            <div className="space-y-3">
              {searchAnalytics?.daily_searches?.map((day, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground w-24">{day.date}</span>
                  <div className="flex-1 bg-background/50 rounded-full h-6 overflow-hidden">
                    <div 
                      className="bg-primary h-full rounded-full transition-all duration-500"
                      style={{ 
                        width: `${Math.min(100, (day.count / Math.max(...searchAnalytics.daily_searches.map(d => d.count), 1)) * 100)}%` 
                      }}
                    />
                  </div>
                  <span className="text-sm font-medium text-foreground w-12 text-right">{day.count}</span>
                </div>
              ))}
              {(!searchAnalytics?.daily_searches || searchAnalytics.daily_searches.length === 0) && (
                <p className="text-muted-foreground text-center py-4">No data available</p>
              )}
            </div>
          </Card>

          {/* Top Queries */}
          <Card className="p-6 rounded-xl border bg-card/50 backdrop-blur-md" data-testid="top-queries-card">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="text-primary" size={20} />
              <h2 className="text-lg font-heading font-semibold text-foreground">Top Queries</h2>
            </div>
            <div className="space-y-3">
              {searchAnalytics?.top_queries?.map((query, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-background/30 hover:bg-background/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-primary">#{idx + 1}</span>
                    <span className="text-sm text-foreground truncate max-w-xs">{query.query}</span>
                  </div>
                  <span className="text-sm text-muted-foreground">{query.count} searches</span>
                </div>
              ))}
              {(!searchAnalytics?.top_queries || searchAnalytics.top_queries.length === 0) && (
                <p className="text-muted-foreground text-center py-4">No queries yet</p>
              )}
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Searches by Model */}
          <Card className="p-6 rounded-xl border bg-card/50 backdrop-blur-md" data-testid="by-model-card">
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="text-primary" size={20} />
              <h2 className="text-lg font-heading font-semibold text-foreground">Searches by Model</h2>
            </div>
            <div className="space-y-3">
              {Object.entries(searchAnalytics?.by_model || {}).map(([model, count], idx) => (
                <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-background/30">
                  <span className="text-sm font-medium text-foreground capitalize">{model}</span>
                  <span className="text-sm text-muted-foreground">{count} searches</span>
                </div>
              ))}
              {Object.keys(searchAnalytics?.by_model || {}).length === 0 && (
                <p className="text-muted-foreground text-center py-4">No data available</p>
              )}
            </div>
          </Card>

          {/* Users by Role */}
          <Card className="p-6 rounded-xl border bg-card/50 backdrop-blur-md" data-testid="by-role-card">
            <div className="flex items-center gap-3 mb-6">
              <Users className="text-primary" size={20} />
              <h2 className="text-lg font-heading font-semibold text-foreground">Users by Role</h2>
            </div>
            <div className="space-y-3">
              {Object.entries(userAnalytics?.by_role || {}).map(([role, count], idx) => (
                <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-background/30">
                  <span className="text-sm font-medium text-foreground capitalize">{role.replace('_', ' ')}</span>
                  <span className="text-sm text-muted-foreground">{count} users</span>
                </div>
              ))}
              {Object.keys(userAnalytics?.by_role || {}).length === 0 && (
                <p className="text-muted-foreground text-center py-4">No data available</p>
              )}
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AnalyticsPage;
